/**
 * Device Fingerprinting Service
 * Collects browser and device characteristics for security tracking
 */

export interface DeviceFingerprintData {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timezoneOffset: number;
  timezone: string;
  colorDepth: number;
  pixelRatio: number;
  cookieEnabled: boolean;
  doNotTrack: boolean | null;
  webgl: string | null;
  canvas: string | null;
  fonts: string[];
  plugins: string[];
  touchSupport: boolean;
  hardwareConcurrency: number;
  maxTouchPoints: number;
  deviceMemory?: number;
  connection?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

export class DeviceFingerprintService {
  /**
   * Generate a comprehensive device fingerprint
   */
  static async generateFingerprint(): Promise<DeviceFingerprintData> {
    const fingerprint: DeviceFingerprintData = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezoneOffset: new Date().getTimezoneOffset(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      colorDepth: screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack:
        navigator.doNotTrack === '1'
          ? true
          : navigator.doNotTrack === '0'
            ? false
            : null,
      webgl: this.getWebGLFingerprint(),
      canvas: this.getCanvasFingerprint(),
      fonts: await this.getAvailableFonts(),
      plugins: this.getPlugins(),
      touchSupport: 'ontouchstart' in window,
      hardwareConcurrency: navigator.hardwareConcurrency || 1,
      maxTouchPoints: navigator.maxTouchPoints || 0,
    };

    // Add optional properties if available
    if ('deviceMemory' in navigator) {
      fingerprint.deviceMemory = (navigator as any).deviceMemory;
    }

    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      fingerprint.connection = {
        effectiveType: connection?.effectiveType,
        downlink: connection?.downlink,
        rtt: connection?.rtt,
      };
    }

    return fingerprint;
  }

  /**
   * Generate a unique device ID hash from fingerprint data
   */
  static async generateDeviceId(
    fingerprint?: DeviceFingerprintData
  ): Promise<string> {
    const data = fingerprint || (await this.generateFingerprint());

    // Create a stable string representation
    const fingerprintString = [
      data.userAgent,
      data.language,
      data.platform,
      data.screenResolution,
      data.timezoneOffset.toString(),
      data.timezone,
      data.colorDepth.toString(),
      data.pixelRatio.toString(),
      data.cookieEnabled.toString(),
      data.webgl || '',
      data.canvas || '',
      data.fonts.join(','),
      data.plugins.join(','),
      data.touchSupport.toString(),
      data.hardwareConcurrency.toString(),
      data.maxTouchPoints.toString(),
      data.deviceMemory?.toString() || '',
      data.connection?.effectiveType || '',
    ].join('|');

    // Generate hash using SubtleCrypto (more secure than simple hash)
    const encoder = new TextEncoder();
    const data_buffer = encoder.encode(fingerprintString);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data_buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    return hashHex.slice(0, 32); // Return first 32 characters
  }

  /**
   * Get WebGL fingerprint
   */
  private static getWebGLFingerprint(): string | null {
    try {
      const canvas = document.createElement('canvas');
      const gl =
        canvas.getContext('webgl') ||
        (canvas.getContext(
          'experimental-webgl'
        ) as WebGLRenderingContext | null);

      if (!gl) return null;

      const renderer = gl.getParameter(gl.RENDERER);
      const vendor = gl.getParameter(gl.VENDOR);
      const version = gl.getParameter(gl.VERSION);
      const shadingLanguageVersion = gl.getParameter(
        gl.SHADING_LANGUAGE_VERSION
      );

      return `${renderer}|${vendor}|${version}|${shadingLanguageVersion}`;
    } catch (e) {
      return null;
    }
  }

  /**
   * Get Canvas fingerprint
   */
  private static getCanvasFingerprint(): string | null {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) return null;

      // Draw a simple pattern that varies by rendering engine
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillStyle = 'rgba(255, 0, 255, 0.5)';
      ctx.fillRect(0, 0, 100, 100);
      ctx.fillStyle = 'rgba(0, 255, 255, 0.7)';
      ctx.fillText('Device fingerprint test 🔒', 2, 2);
      ctx.strokeStyle = 'rgb(255, 0, 0)';
      ctx.arc(50, 50, 25, 0, Math.PI * 2);
      ctx.stroke();

      return canvas.toDataURL().slice(0, 100); // First 100 chars of data URL
    } catch (e) {
      return null;
    }
  }

  /**
   * Get available system fonts (approximate detection)
   */
  private static async getAvailableFonts(): Promise<string[]> {
    const testFonts = [
      'Arial',
      'Arial Black',
      'Calibri',
      'Cambria',
      'Comic Sans MS',
      'Courier New',
      'Georgia',
      'Helvetica',
      'Impact',
      'Lucida Console',
      'Palatino',
      'Tahoma',
      'Times New Roman',
      'Trebuchet MS',
      'Verdana',
      'Monaco',
      'Menlo',
      'Consolas',
      'DejaVu Sans',
      'Liberation Sans',
    ];

    const availableFonts: string[] = [];
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const baselineFont = 'monospace';

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return [];

    // Get baseline measurements
    context.font = `${testSize} ${baselineFont}`;
    const baselineWidth = context.measureText(testString).width;

    for (const font of testFonts) {
      context.font = `${testSize} ${font}, ${baselineFont}`;
      const width = context.measureText(testString).width;

      if (width !== baselineWidth) {
        availableFonts.push(font);
      }
    }

    return availableFonts;
  }

  /**
   * Get browser plugins
   */
  private static getPlugins(): string[] {
    const plugins: string[] = [];

    for (let i = 0; i < navigator.plugins.length; i++) {
      const plugin = navigator.plugins[i];
      plugins.push(plugin.name);
    }

    return plugins.sort();
  }

  /**
   * Get geographic information (if geolocation permission granted)
   */
  static async getLocationInfo(): Promise<{
    latitude?: number;
    longitude?: number;
    accuracy?: number;
  } | null> {
    return new Promise(resolve => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        () => {
          resolve(null); // Permission denied or error
        },
        { timeout: 5000, maximumAge: 300000 } // 5 second timeout, 5 minute cache
      );
    });
  }

  /**
   * Check if this appears to be a trusted device based on stored info
   */
  static isTrustedDevice(deviceId: string): boolean {
    const trustedDevices = JSON.parse(
      localStorage.getItem('trusted_devices') || '[]'
    );
    return trustedDevices.includes(deviceId);
  }

  /**
   * Mark device as trusted
   */
  static markAsTrusted(deviceId: string): void {
    const trustedDevices = JSON.parse(
      localStorage.getItem('trusted_devices') || '[]'
    );
    if (!trustedDevices.includes(deviceId)) {
      trustedDevices.push(deviceId);
      localStorage.setItem('trusted_devices', JSON.stringify(trustedDevices));
    }
  }

  /**
   * Remove device trust
   */
  static removeTrust(deviceId: string): void {
    const trustedDevices = JSON.parse(
      localStorage.getItem('trusted_devices') || '[]'
    );
    const filtered = trustedDevices.filter((id: string) => id !== deviceId);
    localStorage.setItem('trusted_devices', JSON.stringify(filtered));
  }
}
